import React, { useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_LABEL = {
  new: "Under Review", under_review: "Under Review", assigned: "Under Review",
  in_progress: "Advisory in progress", completed: "Completed", closed: "Completed",
};

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function ExpertEngagementCard({ request, onUpdated }) {
  const [note, setNote] = useState("");
  const [sharing, setSharing] = useState(false);
  const isClosed = request.status === "completed" || request.status === "closed";
  const label = STATUS_LABEL[request.status] || "Under Review";

  const shareUpdate = async () => {
    if (!note.trim() || sharing) return;
    setSharing(true);
    const res = await api.expertShareUpdate(request.id, note.trim());
    setSharing(false);
    if (res.ok) { setNote(""); onUpdated(res.data?.request); }
  };

  return (
    <div data-testid="expert-engagement-card" className="max-w-2xl border border-slate-200 rounded-2xl bg-white p-7 md:p-8 space-y-6">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Your focus</div>
        <p className="mt-1.5 text-[14.5px] text-[#0F1B2D] leading-relaxed">{request.request_text}</p>
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</div>
          <div data-testid="expert-engagement-status" className="mt-1 text-[13.5px] font-semibold text-[#1E3A5F]">{label}</div>
        </div>
        {request.engagement_started_at && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Engagement</div>
            <div className="mt-1 text-[13.5px] text-[#0F1B2D]">
              30-day Expert engagement · {fmtDate(request.engagement_started_at)} – {fmtDate(request.engagement_ends_at)}
            </div>
          </div>
        )}
        {request.next_follow_up_at && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Follow-up</div>
            <div data-testid="expert-follow-up-date" className="mt-1 text-[13.5px] text-[#0F1B2D]">{fmtDate(request.next_follow_up_at)}</div>
          </div>
        )}
      </div>

      {(request.resources || []).length > 0 && (
        <div data-testid="expert-resources-section">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Resources from C-ORE Expert</div>
          <div className="mt-2 space-y-2">
            {request.resources.map((r) => (
              <a key={r.id} href={r.url} target="_blank" rel="noreferrer" data-testid={`expert-resource-${r.id}`}
                className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 px-3.5 py-2.5 hover:border-slate-300 transition group">
                <div>
                  <div className="text-[13px] font-semibold text-[#0F1B2D]">{r.title}</div>
                  {r.description && <div className="text-[12px] text-slate-500 mt-0.5">{r.description}</div>}
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#1E3A5F] shrink-0 mt-1" />
              </a>
            ))}
          </div>
        </div>
      )}

      {!isClosed && (
        <div data-testid="expert-share-update-section">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Share an update</div>
          <textarea
            data-testid="expert-update-text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Share progress or new context for the C-ORE Expert."
            className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
          />
          <button
            data-testid="expert-share-update-btn"
            onClick={shareUpdate}
            disabled={!note.trim() || sharing}
            className="mt-2 rounded-full bg-[#1E3A5F] text-white px-5 py-2 text-[12.5px] font-semibold disabled:opacity-50 hover:opacity-90 transition"
          >
            {sharing ? "Sharing…" : "Share an update"}
          </button>
        </div>
      )}

      {(request.updates || []).length > 0 && (
        <div data-testid="expert-updates-timeline">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Updates</div>
          <div className="mt-2 space-y-2.5">
            {request.updates.map((u) => (
              <div key={u.id} className="text-[13px] text-[#0F1B2D] border-l-2 border-slate-200 pl-3">
                <span className="text-slate-400">{fmtDate(u.created_at)} · </span>{u.note}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
